import { useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Filters } from '@aimsos/ui';

/** Stable reference — `Filters` is `memo()`-wrapped; an inline object re-renders it every time. */
const VIEW_TOGGLE_HIDDEN = { hidden: true };

/**
 * The toolbar for EVERY list in this domain — the entity list and every dynamic
 * profile tab. They render the same rows through the same card from the same
 * normalized contract, so they get the same toolbar rather than two that drift.
 *
 * The design system's `Filters` bar, same as the Widget Library's, not a
 * hand-rolled row of selects.
 *
 * Every chip is built from `filterDefs`, which arrived with the rows. This
 * component holds no filter state and narrows nothing — it only translates the
 * hook's state into `Filters` props.
 *
 * Labels come from the payload, NOT from i18n: they are a tenant's own field
 * names from Data Studio, so there is no key to translate. Only the bar's own
 * chrome — the search placeholder, "All filters", "Clear" — is translated.
 * A list may name what it searches (`searchPlaceholder`, e.g. "Search traits...").
 */
function DataListFilterBar({ filtersState, filterDefs, sortDefs, onAllFiltersClick, searchPlaceholder }) {
  const { t } = useTranslation('unified-profiles');
  const {
    searchTerm,
    setSearchTerm,
    selected,
    setFilter,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    defaultSortField,
    defaultSortDirection,
    clearAll,
    activeFilterCount,
  } = filtersState;

  const searchProps = useMemo(
    () => ({
      value: searchTerm,
      placeholder: searchPlaceholder ?? t('list.searchPlaceholder'),
      onChange: (event) => setSearchTerm(event?.target?.value ?? event ?? ''),
      onClear: () => setSearchTerm(''),
    }),
    [searchTerm, setSearchTerm, searchPlaceholder, t],
  );

  // `Filters` holds each slot's value as string[], which is exactly how the
  // request carries it — so there is no scalar/array adapter to get wrong.
  const slots = useMemo(
    () =>
      filterDefs.map((def) => ({
        id: def.id,
        label: def.label,
        options: def.options,
        value: selected[def.id] ?? [],
        onChange: (next) => setFilter(def.id, next),
      })),
    [filterDefs, selected, setFilter],
  );

  const sort = useMemo(
    () =>
      sortDefs.length
        ? {
            label: t('list.sort.label'),
            value: sortField,
            defaultValue: defaultSortField,
            options: sortDefs,
            onChange: setSortField,
            direction: sortDirection,
            defaultDirection: defaultSortDirection,
            onDirectionChange: setSortDirection,
          }
        : null,
    [
      sortDefs,
      sortField,
      sortDirection,
      defaultSortField,
      defaultSortDirection,
      setSortField,
      setSortDirection,
      t,
    ],
  );

  const i18n = useMemo(
    () => ({
      // `Filters`' "All filters" button has no badge slot, so the count of
      // active dimensions rides in the label — same as the Widget Library.
      allFilters: activeFilterCount
        ? t('list.filters.allFiltersWithCount', { count: activeFilterCount })
        : t('list.filters.allFilters'),
      clearFilters: t('list.filters.clearAll'),
      sortAsc: t('list.sort.ascending'),
      sortDesc: t('list.sort.descending'),
    }),
    [activeFilterCount, t],
  );

  return (
    <Filters
      searchProps={searchProps}
      filters={slots}
      sort={sort}
      viewToggle={VIEW_TOGGLE_HIDDEN}
      // Below 1280px `Filters` hides the chips AND the sort control, leaving
      // only search and this button — so the panel is the only way to filter
      // or reorder on a laptop, not a nicety.
      onAllFiltersClick={onAllFiltersClick}
      onClearAll={clearAll}
      i18n={i18n}
    />
  );
}

export default DataListFilterBar;
